// File: frontend/src/pages/UserProfile.tsx
// Fixed to use refactored API and hooks with React Query

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  useUpdateProfile, 
  useChangePassword, 
  useUploadProfileImage,
  useCurrentUser 
} from '@/hooks/useAuth';
import { UserProfileManagement } from '@/components/Profile';
import LoadingSpinner from '@/components/LoadingSpinner';
import { toast } from 'react-hot-toast';

interface ProfileUpdateData {
  first_name: string;
  last_name: string;
  username: string;
  phone_number?: string;
  preferred_language?: string;
}

interface PasswordUpdateData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const UserProfile: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);

  // React Query hooks for API operations
  const { data: currentUserData, isLoading: isLoadingUser } = useCurrentUser();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const uploadImageMutation = useUploadProfileImage();

  // Use the most up-to-date user data
  const userData = currentUserData || user;

  if (isLoading || isLoadingUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Please log in to view your profile</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleUpdateProfile = async (data: ProfileUpdateData) => {
    setIsUpdating(true);
    try {
      await updateProfileMutation.mutateAsync(data);
      toast.success('Profile updated successfully');
      return { success: true, message: 'Profile updated successfully' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update profile';
      toast.error(errorMessage);
      return { 
        success: false, 
        message: errorMessage,
        errors: error.errors || {} 
      };
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePassword = async (data: PasswordUpdateData) => {
    setIsUpdating(true);
    try {
      // Convert to the expected format for the API
      const passwordData = {
        current_password: data.current_password,
        password: data.new_password,
        password_confirm: data.confirm_password
      };
      
      await changePasswordMutation.mutateAsync(passwordData);
      toast.success('Password updated successfully');
      return { success: true, message: 'Password updated successfully' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update password';
      toast.error(errorMessage);
      return { 
        success: false, 
        message: errorMessage,
        errors: error.errors || {} 
      };
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateAvatar = async (file: File) => {
    setIsUpdating(true);
    try {
      const result = await uploadImageMutation.mutateAsync(file);
      toast.success('Avatar updated successfully');
      return { 
        success: true, 
        message: 'Avatar updated successfully',
        url: result.image_url 
      };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update avatar';
      toast.error(errorMessage);
      return { 
        success: false, 
        message: errorMessage
      };
    } finally {
      setIsUpdating(false);
    }
  };

  const handleGoBack = () => {
    // Navigate back to dashboard based on user role
    switch (userData.role) {
      case 'admin':
      case 'super_admin':
        navigate('/dashboard');
        break;
      case 'provider':
        navigate('/dashboard');
        break;
      case 'customer':
      case 'user':
        navigate('/account');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // Check if any mutations are loading
  const isMutationLoading = 
    updateProfileMutation.isPending || 
    changePasswordMutation.isPending || 
    uploadImageMutation.isPending || 
    isUpdating;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Improved Header - Mobile First */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4 lg:py-6">
            <button
              onClick={handleGoBack}
              className="mr-3 sm:mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              disabled={isMutationLoading}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Profile Settings</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your account information and preferences</p>
            </div>
            {isMutationLoading && (
              <div className="flex items-center text-sm text-gray-500 ml-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="hidden sm:inline">Updating...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Content - Improved responsive container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <UserProfileManagement
          user={userData}
          onUpdateProfile={handleUpdateProfile}
          onUpdatePassword={handleUpdatePassword}
          onUpdateAvatar={handleUpdateAvatar}
          isLoading={isMutationLoading}
        />
      </div>

      {/* Loading Overlay */}
      {isMutationLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center">
              <Loader2 className="h-6 w-6 animate-spin mr-3 text-blue-600" />
              <span className="text-gray-700">Updating profile...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;